/**
 * Fullscreen reader — what one touch on the page meant.
 *
 * A phone or a tablet has no arrow keys and no pager under a thumb, so the paper
 * itself has to be the control: a flick turns the page, a drag carries it, and a tap
 * takes the reader's furniture away and gives it back. All three are the *same* touch
 * on the *same* surface, so they are read here, together — two handlers each guessing
 * what the other was about to do is how a tap turns into a page turn.
 *
 * Four rules, and why each number is what it is:
 *
 *  - **a tap is a touch that stayed put** (`TAP_SLOP`, ten pixels). A finger is not
 *    a pixel: it lands on a spot and stays there, and ten is comfortably inside
 *    what a hand does when it means to press something rather than move it;
 *  - **a flick has to travel** (`SWIPE_SLOP`, forty-four — the same distance the
 *    document card's own pager calls a swipe, so one flick means one thing in both
 *    surfaces). The page has to move far enough to be unmistakable before it turns,
 *    or an unsteady press would page a document — and a page that turned under a
 *    reader's thumb would turn again when they tried to put it back. The card also
 *    takes a *fast* flick of only twenty; the reader does not, because the card's
 *    turn is a preview and the reader's is a door: a flick that barely moved should
 *    not commit somebody to answering a page's questions;
 *  - **the flick has to be the bigger half of the gesture, across the pages.** A
 *    reader carries the paper along the pages and pans a zoomed page in both
 *    directions; a gesture that travelled further along the pages than across them
 *    is one of those, and reading it as a page turn would take the paper out from
 *    under the hands that were moving it. Which half is which is the pages' own
 *    business, and it is the `paging` argument: pages that run down are turned by a
 *    sideways flick (and dragged up and down to carry them), pages that run across
 *    are turned by an up-and-down flick and dragged sideways;
 *  - **which of the two a moving touch is doing is that same comparison, read the
 *    other way round** (`readPageDrag`), because a surface has to know *while the
 *    finger is down*: one gesture carries the run of pages, and the other drags the
 *    paper across it, and they are two different movements rather than two ways to
 *    one page.
 *
 * What a touch *means* is decided by distance and never by time. The same flick
 * made by a fast hand and by a slow one is the same flick, and a clock would turn
 * one of them into a page and the other into nothing — which is the difference
 * between a reader who trusts the gesture and one who tries it twice.
 *
 * What to *do* with the answer is the reader's business, and deliberately not this
 * module's: a `next` is the same door the forward arrow takes (see `quizGate`), so
 * a page's questions cannot be walked around by leaning on the screen instead of
 * pressing the button.
 */

/** How far a touch may travel and still be a tap. */
export const TAP_SLOP = 10

/** How far a flick has to travel before it is a page turn. */
export const SWIPE_SLOP = 44

/** What a touch on the page meant — `none` being a drag that was neither. */
export type PageTouch = 'previous' | 'next' | 'tap' | 'none'

/**
 * Which way a document's pages run — the axis a *carry* travels along.
 *
 * `y` is a stack of pages read downwards and `x` is a strip of them read across, and
 * the reader chooses: the paged view and the continuous one both lay their pages out
 * along this axis, and the flick that turns a page is the one across it (see
 * `readPageTouch`).
 */
export type PageAxis = 'x' | 'y'

/** Which of the page's two gestures a moving touch has turned out to be. */
export type PageDrag = 'carry' | 'flick'

/**
 * Which of the page's two gestures a moving touch is doing, if either yet.
 *
 * The page is two controls in one surface: the paper is *carried* along the run of
 * pages — one page at a time, which is what a page turn moves — and *flicked across*
 * it to ask for the page after this one. Both are one finger on one sheet, and the
 * only thing that tells them apart is which way the hand went, so the reading lives
 * here beside the flick's own rule and the surfaces that act on it cannot disagree
 * about a gesture: the reader's paged view claims both (`SnapReader`), and its root
 * reads the flick on the pages it still owns it for.
 *
 * `null` means the touch has not moved far enough to be either yet: it is still a
 * press, and a surface reading one leaves it alone — the reader's root, which reads a
 * press as the tap that puts its furniture away.
 *
 * A dead-level gesture is a *flick*, deliberately: the carry has to win the axis to
 * take the paper, exactly as `readPageTouch` asks a turn to win it, so the two
 * readings agree about every touch and neither is a coin toss at 45 degrees.
 */
export function readPageDrag({
  dx,
  dy,
  paging = 'y',
}: {
  dx: number
  dy: number
  paging?: PageAxis
}): PageDrag | null {
  const turn = paging === 'y' ? dx : dy
  const carry = paging === 'y' ? dy : dx
  if (Math.abs(turn) < TAP_SLOP && Math.abs(carry) < TAP_SLOP) return null
  return Math.abs(carry) > Math.abs(turn) ? 'carry' : 'flick'
}

/**
 * Read one touch, from where it started to where it ended.
 *
 * `dx` is the sideways travel, negative towards the left, and `dy` the vertical one,
 * negative upwards. The paper follows the finger the way paper does, whichever way the
 * pages run: dragging it leftwards (or upwards, when the pages run across) brings the
 * page *after* this one — the direction the forward arrow takes the reader, so the two
 * controls agree about which way is on.
 *
 * `paging` is which way the pages run, and it decides which half of the touch is the
 * flick and which is the carry. It defaults to `y` — pages down, turned by a sideways
 * flick, which is what the reader did before it could be told otherwise — and the two
 * readings are mirrors of each other rather than two rules.
 */
export function readPageTouch({
  dx,
  dy,
  paging = 'y',
}: {
  dx: number
  dy: number
  paging?: PageAxis
}): PageTouch {
  const turn = paging === 'y' ? dx : dy
  const carry = paging === 'y' ? dy : dx
  if (Math.abs(dx) < TAP_SLOP && Math.abs(dy) < TAP_SLOP) return 'tap'
  if (Math.abs(turn) < SWIPE_SLOP) return 'none'
  if (Math.abs(turn) <= Math.abs(carry)) return 'none'
  return turn < 0 ? 'next' : 'previous'
}
